import { helperCreateObjectUrl, type IFileType } from "./fileHelpers";
import type { Resolution } from "./types";
import { getImageDimensionsFromFile } from "./validateImages";

/**
 * Resizes an image to a specific height while maintaining aspect ratio.
 * @param {IFileType} imageFile - The image file to resize.
 * @param {number} newHeight - The desired height of the image.
 * @param {number} newWidth - The desired height of the image.
 * @return {Promise<IFileType | null>} A promise that resolves with the resized image file.
 */
export function resizeImageWithCanvas(
	imageFile: IFileType,
	newHeight: number,
	newWidth: number
): Promise<IFileType | null> {
	return new Promise((resolve, _reject) => {
		// Create an Image to read the uploaded file
		const img = new Image();
		img.onload = () => {
			// Calculate new width to maintain aspect ratio
			// Create a canvas and resize the image
			const canvas = document.createElement("canvas");
			canvas.width = newWidth;
			canvas.height = newHeight;
			const ctx = canvas.getContext("2d");
			if (!ctx) {
				resolve(null);
				return;
			}
			ctx.drawImage(img, 0, 0, newWidth, newHeight);
			const imageData = ctx.getImageData(0, 0, newWidth, newHeight);
			const pixels = imageData.data;
			const numPixels = pixels.length;

			ctx.clearRect(0, 0, newWidth, newHeight);

			for (let i = 0; i < numPixels; i++) {
				if (pixels[i * 4 + 3] <= 128) pixels[i * 4 + 3] = 0;
			}
			ctx.putImageData(imageData, 0, 0);
			// Convert canvas to blob and then to File
			canvas.toBlob((blob) => {
				if (!blob) {
					resolve(null);
					return;
				}
				const resizedFile = new File([blob], imageFile.name, {
					type: imageFile.type,
					lastModified: Date.now()
				});
				resolve(resizedFile);
			}, imageFile.type);
		};

		img.src = helperCreateObjectUrl(imageFile);
	});
}
/**
 * @description parse string eg 512x512 to js size object
 */
export function parseStringSizeResolution(resolution: string, separator = "x"): Resolution | null {
	const sizes = resolution.split(separator);
	if (sizes.length !== 2) {
		return null;
	}
	const width = sizes[0];
	const height = sizes[1];

	if (Number.isNaN(Number(height)) || Number.isNaN(Number(width))) {
		return null;
	}
	return {
		height: Number(height),
		width: Number(width)
	};
}

export function scaleProportion(resolution: Resolution, target: Resolution): Resolution {
	let height = resolution.height;
	let width = resolution.width;
	if (height > target.height) {
		height = target.height;
		width *= height / resolution.height;
	}
	if (width > target.width) {
		const prevWidth = width;
		width = target.width;
		height *= width / prevWidth;
	}
	return { height, width };
}

export const sizeImageInLimits = async (
	image: IFileType,
	maxLimits: {
		heightLimit: number;
		widthLimit: number;
	},
	minLimits: {
		heightLimit: number;
		widthLimit: number;
	}
) => {
	const dm = await getImageDimensionsFromFile(image);
	if (dm === null) {
		return image;
	}
	const newSize = scaleResolutionInBoundByMaxDm(
		dm,
		{
			height: minLimits.heightLimit,
			width: minLimits.widthLimit
		},
		{
			height: maxLimits.heightLimit,
			width: maxLimits.widthLimit
		}
	);
	if (isResolutionEquals(newSize, dm)) {
		return image;
	}
	return resizeImageWithCanvas(image, newSize.height, newSize.width);
};

export function scaleImageToTargetRatio(
	imageSize: Resolution,
	targetratio: Resolution
): Resolution {
	let { height, width } = imageSize;
	const currentRatio = imageSize.width / imageSize.height;
	const targetRatio = targetratio.width / targetratio.height;

	if (currentRatio < targetRatio) {
		// Image is taller than target ratio, scale based on width
		width = height * targetRatio;
	} else {
		// Image is wider than or equal to target ratio, scale based on height
		height = width / targetRatio;
	}

	return {
		width,
		height
	};
}

/**
 * scales resolution in bound, scales image less then min by it's smallest side
 * @param imageSize
 * @param minResolution
 * @param maxResolution
 * @returns
 */
export function scaleResolutionInBound(
	imageSize: Resolution,
	minResolution: Resolution,
	maxResolution: Resolution
): Resolution {
	let { width, height } = imageSize;

	// Check if image is smaller than minimum resolution
	if (width < minResolution.width || height < minResolution.height) {
		const scaleX = minResolution.width / width;
		const scaleY = minResolution.height / height;
		const scale = Math.max(scaleX, scaleY);

		width = Math.round(width * scale);
		height = Math.round(height * scale);
	}

	// Check if image is larger than maximum resolution
	if (width > maxResolution.width || height > maxResolution.height) {
		const scaleX = maxResolution.width / width;
		const scaleY = maxResolution.height / height;
		const scale = Math.min(scaleX, scaleY);

		width = Math.round(width * scale);
		height = Math.round(height * scale);
	}

	return { width, height };
}

/**
 * scales resolution in bound, scales imag less then min by it's largest side
 * @param imageSize
 * @param minResolution
 * @param maxResolution
 * @returns
 */
export function scaleResolutionInBoundByMaxDm(
	imageSize: Resolution,
	minResolution: Resolution,
	maxResolution: Resolution
): Resolution {
	let { width, height } = imageSize;

	// Check if image is smaller than minimum resolution
	if (width < minResolution.width && height < minResolution.height) {
		const scaleX = minResolution.width / width;
		const scaleY = minResolution.height / height;
		const scale = Math.min(scaleX, scaleY);

		width = Math.round(width * scale);
		height = Math.round(height * scale);
	}

	// Check if image is larger than maximum resolution
	if (width > maxResolution.width || height > maxResolution.height) {
		const scaleX = maxResolution.width / width;
		const scaleY = maxResolution.height / height;
		const scale = Math.min(scaleX, scaleY);

		width = Math.round(width * scale);
		height = Math.round(height * scale);
	}

	return { width, height };
}

export function isResolutionEquals(resolution1: Resolution, resolution2: Resolution): boolean {
	return resolution1.width === resolution2.width && resolution1.height === resolution2.height;
}

/**
 * Converts an image file to PNG format while preserving alpha channel transparency.
 * This is useful for clipboard operations which may not support all image formats.
 * @param {IFileType} imageFile - The image file to convert.
 * @return {Promise<IFileType | null>} A promise that resolves with the PNG image file.
 */
export function convertImageToPng(imageFile: IFileType): Promise<IFileType | null> {
	return new Promise((resolve, _reject) => {
		// If already PNG, return as is
		if (imageFile.type === "image/png") {
			resolve(imageFile);
			return;
		}

		const img = new Image();
		img.onload = () => {
			// Create canvas with original dimensions
			const canvas = document.createElement("canvas");
			canvas.width = img.naturalWidth;
			canvas.height = img.naturalHeight;

			const ctx = canvas.getContext("2d");
			if (!ctx) {
				resolve(null);
				return;
			}

			// Draw image on canvas (this preserves alpha channel)
			ctx.drawImage(img, 0, 0);

			// Convert to PNG blob
			canvas.toBlob((blob) => {
				if (!blob) {
					resolve(null);
					return;
				}

				// Create new File with PNG type
				const pngFile = new File([blob], imageFile.name.replace(/\.[^/.]+$/, ".png"), {
					type: "image/png",
					lastModified: Date.now()
				});

				resolve(pngFile);
			}, "image/png");
		};

		img.onerror = () => {
			resolve(null);
		};

		img.src = helperCreateObjectUrl(imageFile);
	});
}
